// AuthContext — token + user state for the whole app.
// Token persisted in localStorage; axios Authorization header set globally so
// every subsequent api call (vendors, daily-entries, etc.) carries it.
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// iter150-review backlog — shared across every helper that touches the
// JWT in localStorage (useUnreadCount, activeSchool, …). Keep the two
// in sync by importing this constant instead of re-declaring the string.
export const TOKEN_KEY = "pm-poshan-auth-token";
// Sibling key written by lib/activeSchool.js — wipe alongside the auth token
// on session expiry so a stale active-school id from a previous user can't
// leak across logins on a shared browser.
export const ACTIVE_SCHOOL_KEY = "pm-poshan-active-school-id";

const AuthContext = createContext({ user: null, token: null, ready: false, login: async () => {}, register: async () => {}, logout: () => {} });

function applyToken(token) {
  if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  else delete axios.defaults.headers.common["Authorization"];
}

// Centralised "session is no longer valid" cleanup. Pure client-side —
// touches localStorage / sessionStorage / axios defaults only. The
// backend already returns 401 for the offending request; we're just
// nudging the UI back to a clean slate.
// iter149 — also wipe the offline Daily Entry queue + API cache so the
// next person on a shared phone can't see the previous school's data.
export function purgeStaleSession() {
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* private mode etc. */ }
  try { localStorage.removeItem(ACTIVE_SCHOOL_KEY); } catch { /* ignore */ }
  try { sessionStorage.clear(); } catch { /* ignore */ }
  applyToken(null);
  // Fire-and-forget — these dynamic imports keep the main bundle small
  // and avoid a hard dep cycle between AuthContext and the queue / SW
  // registration helpers.
  import("@/lib/dailyEntryQueue")
    .then(({ clearAllQueuedEntries }) => clearAllQueuedEntries())
    .catch(() => { /* idb unavailable */ });
  import("@/lib/swRegister")
    .then(({ purgeApiCache }) => purgeApiCache())
    .catch(() => { /* best-effort */ });
}

// Global axios response interceptor — install ONCE at module load.
// Intercepts any 401 from the backend (e.g. "Token expired",
// "Invalid token") that fires mid-session (after the mount-time
// /auth/me check has already passed) and forces a clean re-login
// instead of surfacing a confusing "Token expired" toast under an
// action button (e.g. Admin › Activate plan).
let _interceptorInstalled = false;
function installAuthInterceptor() {
  if (_interceptorInstalled) return;
  _interceptorInstalled = true;
  axios.interceptors.response.use(
    (r) => r,
    (err) => {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const isAuthFail = status === 401 && (
        detail === "Token expired" ||
        detail === "Invalid token" ||
        detail === "Not authenticated" ||
        detail === "User not found"
      );
      if (isAuthFail && typeof window !== "undefined" && !window.location.pathname.startsWith("/login") && !window.location.pathname.startsWith("/sso/")) {
        purgeStaleSession();
        import("sonner")
          .then(({ toast }) => toast.info("Session expired — please log in again."))
          .catch(() => { /* toast lib unavailable — silent fall-through */ });
        window.location.replace("/login");
      }
      return Promise.reject(err);
    },
  );
}
installAuthInterceptor();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || null);
  const [ready, setReady] = useState(false);

  // On mount, if we have a token, validate it via /auth/me. If invalid
  // (401 from a token signed with an older JWT_SECRET, an expired token,
  // a deleted user, etc.) we wipe the stale session and bounce to /login
  // with a single lightweight toast. We deliberately only fire the toast
  // / redirect when the user is NOT already on /login — otherwise an
  // anonymous visitor hitting /login would see a noisy "Session expired"
  // toast they never had a session for.
  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) { setReady(true); return; }
    applyToken(t);
    axios.get(`${API}/auth/me`)
      .then((r) => { setUser(r.data); setToken(t); })
      .catch(() => {
        purgeStaleSession();
        setUser(null); setToken(null);
        if (typeof window !== "undefined" && window.location.pathname !== "/login" && !window.location.pathname.startsWith("/sso/")) {
          // Lazy-import sonner so the AuthContext stays decoupled from the
          // toast library if it's ever swapped out.
          import("sonner")
            .then(({ toast }) => toast.info("Session expired — please log in again."))
            .catch(() => { /* toast lib unavailable — silent fall-through */ });
          // replace() (not assign) so the back button doesn't bring the
          // user right back to the broken page.
          window.location.replace("/login");
        }
      })
      .finally(() => setReady(true));
  }, []);

  const login = useCallback(async (mobile, password) => {
    const r = await axios.post(`${API}/auth/login`, { mobile, password });
    const { token: t, user: u } = r.data;
    localStorage.setItem(TOKEN_KEY, t);
    applyToken(t);
    setToken(t); setUser(u);
    return u;
  }, []);

  const register = useCallback(async (payload) => {
    const r = await axios.post(`${API}/auth/register`, payload);
    const { token: t, user: u } = r.data;
    localStorage.setItem(TOKEN_KEY, t);
    applyToken(t);
    setToken(t); setUser(u);
    return u;
  }, []);

  // iter161 — Toolkit SSO: adopt a session returned by the exchange without a page reload.
  const adoptSession = useCallback((t, u) => {
    localStorage.setItem(TOKEN_KEY, t);
    applyToken(t);
    setToken(t); setUser(u); setReady(true);
  }, []);

  const logout = useCallback(() => {
    // iter149 — Single teardown path. purgeStaleSession wipes token,
    // active school, offline queue (IDB), and asks the SW to drop its
    // API cache so the next person on a shared phone gets a clean slate.
    purgeStaleSession();
    setUser(null); setToken(null);
    // Hard reload so all in-memory caches reset.
    window.location.href = "/login";
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/me`);
      setUser(r.data);
      return r.data;
    } catch (e) { return null; }
  }, []);

  const value = useMemo(() => ({ user, token, ready, login, register, logout, refreshMe, adoptSession }), [user, token, ready, login, register, logout, refreshMe, adoptSession]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() { return useContext(AuthContext); }

// Format FastAPI 422-style detail (which can be array) into a printable string.
export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
